import { useClient, useSalesReport } from '@/api/hooks';
import { SalesSubnav } from '@/components/sales/SalesSubnav';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { PageHeader } from '@/components/ui/PageHeader';
import { Select } from '@/components/ui/Select';
import { getErrorMessage } from '@/lib/api';
import { cn } from '@/lib/cn';
import { formatDate, todayIso } from '@/lib/format';
import type { SalesSaleType } from '@/types/api';
import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';

const saleTypeLabel: Record<SalesSaleType, string> = {
  live_animal: 'Animal vivo',
  carcass_share: 'Cotas de carcaça',
  retail_cuts: 'Cortes',
};

const statusLabel: Record<string, string> = {
  draft: 'Rascunho',
  open: 'Aberto',
  full: 'Completo',
  slaughtered: 'Abatido',
  delivered: 'Entregue',
};

function money(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

function startOfMonthIso(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function startOfYearIso(d = new Date()): string {
  return `${d.getFullYear()}-01-01`;
}

function monthsAgoIso(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return startOfMonthIso(d);
}

type Preset = 'month' | 'year' | 'last12' | 'custom';

export function SalesReportPage() {
  const { data: client, isLoading: clientLoading } = useClient();
  const [preset, setPreset] = useState<Preset>('year');
  const [from, setFrom] = useState(startOfYearIso());
  const [to, setTo] = useState(todayIso());
  const [saleType, setSaleType] = useState('');
  const [groupBy, setGroupBy] = useState<'month' | 'year'>('month');

  const filters = useMemo(
    () => ({
      from,
      to,
      sale_type: saleType || undefined,
      group_by: groupBy,
    }),
    [from, to, saleType, groupBy],
  );

  const { data: report, isLoading, isFetching, isError, error, refetch } = useSalesReport(filters);

  if (!clientLoading && !client?.modules?.animal_sales) {
    return <Navigate to="/" replace />;
  }

  function applyPreset(next: Preset) {
    setPreset(next);
    if (next === 'month') {
      setFrom(startOfMonthIso());
      setTo(todayIso());
      setGroupBy('month');
    } else if (next === 'year') {
      setFrom(startOfYearIso());
      setTo(todayIso());
      setGroupBy('month');
    } else if (next === 'last12') {
      setFrom(monthsAgoIso(11));
      setTo(todayIso());
      setGroupBy('month');
    }
  }

  const totals = report?.totals;
  const series = report?.series ?? [];
  const maxCollected = Math.max(...series.map((s) => s.revenue_collected), 1);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Relatório de vendas"
        subtitle="Visão consolidada de receita, cotas e funil no período."
      />

      <SalesSubnav />

      <div className="flex flex-wrap gap-2">
        {(
          [
            ['month', 'Este mês'],
            ['year', 'Este ano'],
            ['last12', 'Últimos 12 meses'],
            ['custom', 'Personalizado'],
          ] as const
        ).map(([key, label]) => (
          <Button
            key={key}
            type="button"
            size="sm"
            variant={preset === key ? 'primary' : 'outline'}
            onClick={() => applyPreset(key)}
          >
            {label}
          </Button>
        ))}
      </div>

      <div className="grid gap-3 rounded-2xl border border-manejai-sand bg-white p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Input
          label="De"
          type="date"
          value={from}
          onChange={(e) => {
            setPreset('custom');
            setFrom(e.target.value);
          }}
        />
        <Input
          label="Até"
          type="date"
          value={to}
          onChange={(e) => {
            setPreset('custom');
            setTo(e.target.value);
          }}
        />
        <Select
          label="Tipo de venda"
          value={saleType}
          onChange={(e) => setSaleType(e.target.value)}
          options={[
            { value: '', label: 'Todos' },
            { value: 'live_animal', label: 'Animal vivo' },
            { value: 'carcass_share', label: 'Cotas de carcaça' },
            { value: 'retail_cuts', label: 'Cortes' },
          ]}
        />
        <Select
          label="Agrupar série"
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value as 'month' | 'year')}
          options={[
            { value: 'month', label: 'Por mês' },
            { value: 'year', label: 'Por ano' },
          ]}
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-manejai-bark/60">Carregando relatório…</p>
      ) : isError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <p>{getErrorMessage(error, 'Não foi possível carregar o relatório.')}</p>
          <Button type="button" size="sm" variant="outline" className="mt-3" onClick={() => refetch()}>
            Tentar de novo
          </Button>
        </div>
      ) : !totals ? (
        <p className="text-sm text-manejai-bark/60">Sem dados no período.</p>
      ) : (
        <>
          <div className={cn('grid gap-3 sm:grid-cols-2 lg:grid-cols-4', isFetching && 'opacity-70')}>
            <MetricCard label="Receita recebida" value={money(totals.revenue_collected)} tone="success" />
            <MetricCard label="Pipeline (reservado+pago)" value={money(totals.revenue_pipeline)} />
            <MetricCard label="Em aberto" value={money(totals.revenue_outstanding)} tone="warning" />
            <MetricCard
              label="Cotas vendidas"
              value={String(totals.shares_sold)}
              hint={`${totals.lots_count} lote(s) · ${money(totals.revenue_delivered)} entregue`}
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <section className="rounded-2xl border border-manejai-sand bg-white p-4 lg:col-span-2">
              <h2 className="font-display text-base font-semibold text-manejai-forest">
                Receita no período
              </h2>
              <p className="mt-1 text-xs text-manejai-bark/55">
                Com base na data de pagamento do saldo (ou sinal/reserva, se ainda não pago).
              </p>
              {series.length === 0 ? (
                <p className="mt-6 text-sm text-manejai-bark/55">Nenhuma venda no período.</p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {series.map((row) => (
                    <li key={row.period} className="grid grid-cols-[4.5rem_1fr_auto] items-center gap-3">
                      <span className="text-xs font-medium text-manejai-bark/70">{row.label}</span>
                      <div className="h-2.5 overflow-hidden rounded-full bg-manejai-sand/60">
                        <div
                          className="h-full rounded-full bg-manejai-forest transition-all"
                          style={{
                            width: `${Math.max(4, (row.revenue_collected / maxCollected) * 100)}%`,
                            opacity: row.revenue_collected === 0 ? 0.25 : 1,
                          }}
                        />
                      </div>
                      <span className="text-right text-xs tabular-nums text-manejai-forest">
                        {money(row.revenue_collected)}
                        <span className="block text-[10px] text-manejai-bark/50">
                          {row.shares_sold} cota(s)
                        </span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="space-y-3">
              <div className="rounded-2xl border border-manejai-sand bg-white p-4">
                <h2 className="font-display text-base font-semibold text-manejai-forest">Por tipo</h2>
                <ul className="mt-3 space-y-2">
                  {(report?.by_sale_type ?? []).length === 0 ? (
                    <li className="text-sm text-manejai-bark/55">—</li>
                  ) : (
                    (report?.by_sale_type ?? []).map((row) => (
                      <li
                        key={row.sale_type}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span>
                          {saleTypeLabel[row.sale_type as SalesSaleType] ?? row.sale_type}
                          <span className="block text-xs text-manejai-bark/50">
                            {row.shares_sold} cota(s)
                          </span>
                        </span>
                        <span className="font-medium tabular-nums text-manejai-forest">
                          {money(row.revenue_collected)}
                        </span>
                      </li>
                    ))
                  )}
                </ul>
              </div>

              <div className="rounded-2xl border border-manejai-sand bg-white p-4">
                <h2 className="font-display text-base font-semibold text-manejai-forest">Leads</h2>
                <dl className="mt-3 grid grid-cols-3 gap-2 text-center text-sm">
                  <div>
                    <dt className="text-xs text-manejai-bark/55">Total</dt>
                    <dd className="font-semibold text-manejai-forest">{totals.leads_total}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-manejai-bark/55">Convertidos</dt>
                    <dd className="font-semibold text-manejai-forest">{totals.leads_converted}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-manejai-bark/55">Pendentes</dt>
                    <dd className="font-semibold text-manejai-forest">{totals.leads_pending}</dd>
                  </div>
                </dl>
              </div>
            </section>
          </div>

          <section className="rounded-2xl border border-manejai-sand bg-white">
            <div className="border-b border-manejai-sand px-4 py-3">
              <h2 className="font-display text-base font-semibold text-manejai-forest">
                Lotes no período
              </h2>
            </div>
            {(report?.lots ?? []).length === 0 ? (
              <p className="px-4 py-6 text-sm text-manejai-bark/55">Nenhum lote no período.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-manejai-sand text-left text-xs text-manejai-bark/60">
                      <th className="px-4 py-3 font-medium">Título</th>
                      <th className="px-4 py-3 font-medium">Tipo</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Cotas</th>
                      <th className="px-4 py-3 font-medium">Recebido</th>
                      <th className="px-4 py-3 font-medium">Abate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-manejai-sand/70">
                    {(report?.lots ?? []).map((lot) => (
                      <tr key={lot.id} className="hover:bg-manejai-cream/30">
                        <td className="px-4 py-3">
                          <Link
                            to={`/vendas/${lot.id}`}
                            className="font-medium text-manejai-forest hover:underline"
                          >
                            {lot.title}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-manejai-bark/80">
                          {saleTypeLabel[lot.sale_type as SalesSaleType] ?? lot.sale_type}
                        </td>
                        <td className="px-4 py-3">
                          <Badge>{statusLabel[lot.status] ?? lot.status}</Badge>
                        </td>
                        <td className="px-4 py-3 tabular-nums">
                          {lot.shares_sold}/{lot.shares_count}
                        </td>
                        <td className="px-4 py-3 tabular-nums">{money(lot.revenue_collected)}</td>
                        <td className="px-4 py-3 text-manejai-bark/70">
                          {formatDate(lot.slaughter_date)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function MetricCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'success' | 'warning';
}) {
  return (
    <div className="rounded-2xl border border-manejai-sand bg-white p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-manejai-bark/55">{label}</p>
      <p
        className={cn(
          'mt-1 font-display text-xl font-semibold tabular-nums',
          tone === 'success' && 'text-emerald-800',
          tone === 'warning' && 'text-amber-800',
          !tone && 'text-manejai-forest',
        )}
      >
        {value}
      </p>
      {hint ? <p className="mt-1 text-xs text-manejai-bark/50">{hint}</p> : null}
    </div>
  );
}
